import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../config/supabaseClient';
import { toast } from 'react-hot-toast';
import { ENV } from '../../config/env';

type InputType = 'text' | 'number' | 'select' | 'currency' | 'date';

interface GuidedQuestion {
  id: string;
  section_id: string;
  question_key: string;
  order_index: number;
  prompt: string;
  help_text: string | null;
  input_type: InputType;
  options: any;
  required: boolean;
}

interface GuidedAnswerRow {
  id: string;
  question_id: string;
  answer_jsonb: any;
}

export interface GuidedAnswerPayload {
  questionId: string;
  questionKey: string;
  prompt: string;
  answer: any;
}

interface GuidedSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessPlanId: string;
  sectionId: string;
  sectionTitle: string;
  context: any;
  onApplyDraft: (content: string) => Promise<void>;
}

const GuidedSectionModal: React.FC<GuidedSectionModalProps> = ({
  isOpen,
  onClose,
  businessPlanId,
  sectionId,
  sectionTitle,
  context,
  onApplyDraft,
}) => {
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [questions, setQuestions] = useState<GuidedQuestion[]>([]);
  const [answersByQuestionId, setAnswersByQuestionId] = useState<Record<string, any>>({});

  const saveTimer = useRef<number | null>(null);

  const orderedQuestions = useMemo(() => {
    return [...questions].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  }, [questions]);

  const guidedAnswersPayload: GuidedAnswerPayload[] = useMemo(() => {
    return orderedQuestions.map((q) => ({
      questionId: q.id,
      questionKey: q.question_key,
      prompt: q.prompt,
      answer: answersByQuestionId[q.id],
    }));
  }, [orderedQuestions, answersByQuestionId]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoading(true);
      try {
        const { data: userResp } = await supabase.auth.getUser();
        const userId = userResp.user?.id;
        if (!userId) throw new Error('You must be logged in');

        const { data: qData, error: qErr } = await supabase
          .from('business_plan_section_questions')
          .select('id, section_id, question_key, order_index, prompt, help_text, input_type, options, required')
          .eq('section_id', sectionId)
          .eq('is_active', true);

        if (qErr) throw qErr;
        const qs = (qData || []) as GuidedQuestion[];
        setQuestions(qs);

        if (qs.length === 0) {
          setAnswersByQuestionId({});
          return;
        }

        const { data: aData, error: aErr } = await supabase
          .from('business_plan_section_answers')
          .select('id, question_id, answer_jsonb')
          .eq('business_plan_id', businessPlanId)
          .eq('section_id', sectionId)
          .eq('user_id', userId);

        if (aErr) throw aErr;

        const map: Record<string, any> = {};
        for (const row of (aData || []) as GuidedAnswerRow[]) {
          map[row.question_id] = row.answer_jsonb;
        }
        setAnswersByQuestionId(map);
      } catch (e: any) {
        console.error('Failed to load guided questions/answers:', e);
        toast.error(e?.message || 'Failed to load guided questions');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, sectionId, businessPlanId]);

  const queueSave = (nextAnswers: Record<string, any>) => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }

    saveTimer.current = window.setTimeout(async () => {
      try {
        const { data: userResp } = await supabase.auth.getUser();
        const userId = userResp.user?.id;
        if (!userId) throw new Error('You must be logged in');

        const rows = orderedQuestions.map((q) => ({
          business_plan_id: businessPlanId,
          section_id: sectionId,
          question_id: q.id,
          user_id: userId,
          answer_jsonb: nextAnswers[q.id] ?? {},
          updated_at: new Date().toISOString(),
        }));

        const { error } = await supabase
          .from('business_plan_section_answers')
          .upsert(rows, { onConflict: 'business_plan_id,question_id,user_id' });

        if (error) throw error;
      } catch (e: any) {
        console.error('Failed to save guided answers:', e);
      }
    }, 500);
  };

  const setAnswer = (questionId: string, value: any) => {
    setAnswersByQuestionId((prev) => {
      const next = { ...prev, [questionId]: value };
      queueSave(next);
      return next;
    });
  };

  const renderInput = (q: GuidedQuestion) => {
    const val = answersByQuestionId[q.id];

    if (q.input_type === 'select') {
      const opts: string[] = Array.isArray(q.options) ? q.options : [];
      return (
        <select
          value={typeof val === 'string' ? val : ''}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select…</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }

    if (q.input_type === 'date') {
      return (
        <input
          type="date"
          value={typeof val === 'string' ? val : ''}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }

    if (q.input_type === 'number' || q.input_type === 'currency') {
      return (
        <input
          type="number"
          value={typeof val === 'number' || typeof val === 'string' ? String(val) : ''}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }

    return (
      <textarea
        value={typeof val === 'string' ? val : (val?.text ?? '')}
        onChange={(e) => setAnswer(q.id, e.target.value)}
        rows={3}
        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    );
  };

  const handleDraft = async () => {
    try {
      if (!context || !context.businessIdea) {
        toast.error('Missing business idea context');
        return;
      }

      setDrafting(true);

      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp?.data?.session?.access_token || '';

      const resp = await fetch(`${ENV.API_URL}/api/business-plans/regenerate-section`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          sectionId,
          context: {
            ...context,
            guidedAnswers: guidedAnswersPayload,
          },
        }),
      });

      if (!resp.ok) {
        if (resp.status === 429) {
          const data = await resp.json().catch(() => null);
          throw new Error((data && (data.message || data.error)) || 'Monthly limit reached');
        }
        const text = await resp.text();
        throw new Error(text || 'Failed to draft section');
      }

      const section = await resp.json();
      const content = typeof section?.content === 'string' ? section.content : '';
      if (!content || content.trim().length === 0) {
        throw new Error('Empty draft returned');
      }

      await onApplyDraft(content);
      toast.success('Draft applied to section');
      onClose();
    } catch (e: any) {
      console.error('Failed to draft section:', e);
      toast.error(e?.message || 'Failed to draft section');
    } finally {
      setDrafting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-100">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <div className="text-sm font-semibold text-gray-900">Guided Builder</div>
            <div className="mt-1 text-xs text-gray-600">{sectionTitle}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="max-h-[70vh] overflow-auto px-6 py-5">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading questions…</div>
          ) : orderedQuestions.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No guided questions configured for this section yet.</div>
          ) : (
            <div className="space-y-5">
              {orderedQuestions.map((q) => (
                <div key={q.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {q.prompt}
                        {q.required ? <span className="ml-1 text-red-500">*</span> : null}
                      </div>
                      {q.help_text ? <div className="mt-1 text-xs text-gray-600">{q.help_text}</div> : null}
                    </div>
                  </div>
                  {renderInput(q)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="text-xs text-gray-500">Answers auto-save.</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDraft}
              disabled={drafting || loading || orderedQuestions.length === 0}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
            >
              {drafting ? 'Drafting…' : 'Draft section from answers'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedSectionModal;
